import { AlertProps, Button } from '@chakra-ui/react';
import React from 'react';
import { Link } from 'react-router-dom';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { useMeshRunRecord } from '../../hooks/instanceGraphContext';
import { ActionableAlert } from 'packages/dataPipeline/ui/components/ActionableAlert';

const PipelineGenericAlert = ({
  children,
  status,
}: {
  children: React.ReactNode;
  status: AlertProps['status'];
}) => {
  return (
    <ActionableAlert status={status} mb="10px" mt="10px">
      {children}
    </ActionableAlert>
  );
};

export const PipelineDetailsAlert = ({
  openUpload,
}: {
  openUpload: () => void;
}) => {
  const { status } = useMeshRunRecord();

  switch (status) {
    case 'inProgress':
      return (
        <PipelineGenericAlert status="info">
          Pipeline in progress
        </PipelineGenericAlert>
      );

    case 'error':
      return (
        <PipelineGenericAlert status="error">
          Errors detected in the pipeline. Select the node to diagnose the
          issue.
        </PipelineGenericAlert>
      );

    case 'success':
      return (
        <PipelineGenericAlert status="success">
          Pipeline completed successfully
        </PipelineGenericAlert>
      );

    case 'waitingForInput':
      return (
        <ActionableAlert
          actionElement={
            <Button
              variant="link"
              color="blue.400"
              fontWeight="semibold"
              fontSize="16px"
              onClick={openUpload}
              _focus={{ boxShadow: 'none' }}
            >
              Upload Files
            </Button>
          }
          status="warning"
          mb="10px"
          mt="10px"
        >
          Upload all required files
        </ActionableAlert>
      );

    case 'waitingForAssignment': {
      const assignmentLinkElement = (
        <Link
          to="/assignments"
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          Confirm Assignment
        </Link>
      );

      return (
        <ActionableAlert
          actionElement={assignmentLinkElement}
          status="warning"
          mb="10px"
          mt="10px"
        >
          Assign Receivables
        </ActionableAlert>
      );
    }

    case 'waitingForVerificationApproval':
      return (
        <PipelineGenericAlert status="warning">
          Waiting for verification approval
        </PipelineGenericAlert>
      );

    case 'waitingForReconcileApproval':
      return (
        <PipelineGenericAlert status="warning">
          Reconciliation is awaiting review
        </PipelineGenericAlert>
      );

    case 'expired':
      return (
        <PipelineGenericAlert status="warning">
          This pipeline is expired.
        </PipelineGenericAlert>
      );
    default:
      return finleyAssertUnreachable(status);
  }
};
